import { useCurrentUser } from "../../hooks/useCurrentUser";
import { resolvePhotoUrl, handlePhotoError } from "../../utils/profilePhoto";
import React, { useEffect, useMemo, useState } from "react";
import { MoreHorizontal, Send, Smile, ThumbsUp } from "lucide-react";
import { toast } from "react-toastify";
import {
  getMySurveyEngagement,
  getMySurveyEngagementReplies,
  likeMySurveyComment,
  replyMySurveyComment,
} from "../../service/mySurvey";


const avatarUrl = resolvePhotoUrl;

const formatCommentTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeComment = (comment) => ({
  ...comment,
  comment_id: Number(comment.comment_id ?? comment.id),
  like_count: Number(comment.like_count ?? comment.likes_count ?? 0),
  reply_count: Number(comment.reply_count ?? 0),
  liked_by_me: Boolean(comment.liked_by_me),
});

const responseSucceeded = (response) => Boolean(response?.data?.success ?? response?.data?.status);

const MySurveyCommentsThread = ({ surveyId, onViewAll }) => {
  const { avatar } = useCurrentUser();
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [replies, setReplies] = useState({});
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingReplies, setLoadingReplies] = useState({});
  const [likeLoading, setLikeLoading] = useState({});
  const [replyLoading, setReplyLoading] = useState({});
  const [drafts, setDrafts] = useState({});
  const [replyTargets, setReplyTargets] = useState({});

  useEffect(() => {
    let mounted = true;

    const loadComments = async () => {
      try {
        setLoading(true);
        const response = await getMySurveyEngagement(surveyId);
        if (!responseSucceeded(response)) throw new Error(response?.data?.message || "Failed to load comments.");
        const data = response.data.data || {};
        if (mounted) {
          const nextComments = Array.isArray(data.comments) ? data.comments.map(normalizeComment) : [];
          setComments(nextComments);
          setCommentCount(Number(data.comment_count ?? nextComments.length));
        }
      } catch (error) {
        if (mounted) toast.error(error.response?.data?.message || error.message || "Failed to load comments.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (surveyId) loadComments();
    return () => { mounted = false; };
  }, [surveyId]);

  const visibleComments = useMemo(
    () => (showAll ? comments : comments.slice(0, 2)),
    [comments, showAll]
  );

  const updateComment = (commentId, updater) => {
    setComments((current) => current.map((comment) =>
      Number(comment.comment_id) === Number(commentId) ? updater(comment) : comment
    ));
    setReplies((current) => {
      const next = { ...current };
      Object.keys(next).forEach((rootId) => {
        next[rootId] = (next[rootId] || []).map((comment) =>
          Number(comment.comment_id) === Number(commentId) ? updater(comment) : comment
        );
      });
      return next;
    });
  };

  const loadReplies = async (commentId) => {
    if (replies[commentId]) return;
    try {
      setLoadingReplies((current) => ({ ...current, [commentId]: true }));
      const response = await getMySurveyEngagementReplies(surveyId, commentId);
      if (!responseSucceeded(response)) throw new Error(response?.data?.message || "Failed to load replies.");
      const data = Array.isArray(response.data.data) ? response.data.data.map(normalizeComment) : [];
      setReplies((current) => ({ ...current, [commentId]: data }));
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to load replies.");
    } finally {
      setLoadingReplies((current) => ({ ...current, [commentId]: false }));
    }
  };

  const toggleReplies = async (commentId) => {
    setExpandedComments((current) => {
      const next = new Set(current);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
    if (!replies[commentId]) await loadReplies(commentId);
  };

  const handleLike = async (comment) => {
    const commentId = comment.comment_id;
    if (likeLoading[commentId]) return;
    try {
      setLikeLoading((current) => ({ ...current, [commentId]: true }));
      const response = await likeMySurveyComment(surveyId, commentId);
      if (!responseSucceeded(response)) throw new Error(response?.data?.message || "Failed to update like.");
      const data = response.data.data || {};
      updateComment(commentId, (current) => ({
        ...current,
        liked_by_me: Boolean(data.liked ?? !current.liked_by_me),
        like_count: Number(data.total_likes ?? current.like_count),
      }));
      toast.success(data.liked ? "Comment liked." : "Comment unliked.");
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to update like.");
    } finally {
      setLikeLoading((current) => ({ ...current, [commentId]: false }));
    }
  };

  const handleReply = async (rootComment, targetComment) => {
    const rootId = rootComment.comment_id;
    const draft = (drafts[rootId] || "").trim();
    if (!draft || replyLoading[rootId]) return;
    try {
      setReplyLoading((current) => ({ ...current, [rootId]: true }));
      const response = await replyMySurveyComment(surveyId, rootId, {
        comment: draft,
        reply_to_comment_id: targetComment?.comment_id ?? rootId,
      });
      if (!responseSucceeded(response)) throw new Error(response?.data?.message || "Failed to post reply.");
      const reply = normalizeComment(response.data.data || {});
      setReplies((current) => {
        const currentReplies = current[rootId] || [];
        const targetId = targetComment?.comment_id ?? rootId;
        const targetIndex = currentReplies.findIndex((item) => item.comment_id === targetId);
        const insertAt = targetIndex === -1 ? currentReplies.length : targetIndex + 1;
        return {
          ...current,
          [rootId]: [...currentReplies.slice(0, insertAt), reply, ...currentReplies.slice(insertAt)],
        };
      });
      setComments((current) => current.map((comment) =>
        comment.comment_id === rootId ? { ...comment, reply_count: comment.reply_count + 1 } : comment
      ));
      setCommentCount((current) => current + 1);
      setDrafts((current) => ({ ...current, [rootId]: "" }));
      setReplyTargets((current) => ({ ...current, [rootId]: null }));
      setExpandedComments((current) => new Set(current).add(rootId));
      toast.success(response.data.message || "Reply posted successfully.");
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to post reply.");
    } finally {
      setReplyLoading((current) => ({ ...current, [rootId]: false }));
    }
  };

  const renderReply = (rootComment, reply) => {
    const target = reply.reply_to_comment_id && reply.reply_to_comment_id !== rootComment.comment_id
      ? `@${reply.reply_to_member_name || "member"} `
      : "";
    return (
      <div className="sr-comment-item sr-comment-reply-item" key={reply.comment_id}>
        <img src={avatarUrl(reply.profile_photo)} onError={handlePhotoError} className="sr-comment-avatar" alt="" />
        <div className="sr-comment-content-box">
          <div className="sr-comment-header">
            <span className="sr-comment-author-name">{reply.member_name || "User"}</span>
            <span className="sr-comment-time">{formatCommentTime(reply.created_at)}</span>
          </div>
          <div className="sr-comment-text"><span className="sr-reply-mention">{target}</span>{reply.comment}</div>
          <div className="sr-comment-actions">
            <button type="button" className={`sr-comment-action-btn ${reply.liked_by_me ? "liked" : ""}`} onClick={() => handleLike(reply)}>
              {reply.liked_by_me ? "Unlike" : "Like"}
            </button>
            <button type="button" className="sr-comment-action-btn" onClick={() => setReplyTargets((current) => ({ ...current, [rootComment.comment_id]: reply }))}>
              Reply
            </button>
            {reply.like_count > 0 && <span className="d-flex align-items-center gap-1"><span className="sr-like-circle-badge"><ThumbsUp size={8} fill="currentColor" /></span>{reply.like_count}</span>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="sr-comments-section"><div className="text-secondary ft-12">Loading comments...</div></div>;
  if (comments.length === 0) return <div className="sr-comments-section"><div className="text-secondary ft-12">No comments yet.</div></div>;

  return (
    <div className="sr-comments-section">
      <div className="sr-comments-list">
        {visibleComments.map((comment) => {
          const rootId = comment.comment_id;
          const commentReplies = replies[rootId] || [];
          const target = replyTargets[rootId];
          const isExpanded = expandedComments.has(rootId);
          return (
            <div key={rootId} className="sr-comment-thread">
              <div className="sr-comment-item">
                <img src={avatarUrl(comment.profile_photo)} onError={handlePhotoError} className="sr-comment-avatar" alt="" />
                <div className="sr-comment-content-box">
                  <div className="sr-comment-header">
                    <span className="sr-comment-author-name">{comment.member_name || "User"}</span>
                    <span className="sr-comment-time">{formatCommentTime(comment.created_at)}</span>
                  </div>
                  <div className="sr-comment-text">{comment.comment}</div>
                  <div className="sr-comment-actions">
                    <button type="button" className={`sr-comment-action-btn ${comment.liked_by_me ? "liked" : ""}`} onClick={() => handleLike(comment)}>
                      {comment.liked_by_me ? "Unlike" : "Like"}
                    </button>
                    <button type="button" className="sr-comment-action-btn" onClick={() => setReplyTargets((current) => ({ ...current, [rootId]: comment }))}>
                      Reply
                    </button>
                    {comment.like_count > 0 && <span className="d-flex align-items-center gap-1"><span className="sr-like-circle-badge"><ThumbsUp size={8} fill="currentColor" /></span>{comment.like_count}</span>}
                  </div>
                  {comment.reply_count > 0 && (
                    <button type="button" className="sr-view-replies-btn" onClick={() => toggleReplies(rootId)}>
                      {isExpanded ? "Hide replies" : `View ${comment.reply_count} replies`}
                    </button>
                  )}
                </div>
                <button type="button" className="sr-comment-more-btn" aria-label="More comment actions"><MoreHorizontal size={16} /></button>
              </div>

              {isExpanded && (
                <div className="sr-comment-replies">
                  {loadingReplies[rootId] ? <div className="text-secondary ft-12">Loading replies...</div> : commentReplies.map((reply) => renderReply(comment, reply))}
                </div>
              )}

              <div className="sr-reply-input-wrapper">
                <img src={avatar} onError={handlePhotoError} className="sr-reply-input-avatar" alt="" />
                <div className="sr-reply-input-box">
                  <div className="sr-replying-to-row"><Smile size={14} /><span>{target ? `@${target.member_name || "member"}` : "Reply to this comment"}</span></div>
                  <input type="text" className="sr-reply-input-field" placeholder="Write A Reply..." value={drafts[rootId] || ""} onChange={(event) => setDrafts((current) => ({ ...current, [rootId]: event.target.value }))} onKeyDown={(event) => { if (event.key === "Enter") handleReply(comment, target); }} />
                  <div className="sr-reply-input-icons"><button type="button" className="sr-reply-icon-btn" aria-label="Send reply" onClick={() => handleReply(comment, target)}><Send size={15} /></button></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {commentCount > visibleComments.length && (
        <div className="sr-comments-footer">
          <button type="button" className="sr-comments-view-all-link" onClick={() => (onViewAll ? onViewAll() : setShowAll(true))}>
            View more comments <span aria-hidden="true">-&gt;</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MySurveyCommentsThread;
